import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Plus, Search, X, Wand2, Trophy, CheckSquare, Square, Settings as SettingsIcon, Trash2, Lock, KeyRound, Activity, Filter, Info, Sparkles, Download, Upload, Files, Menu, Award, LayoutGrid } from 'lucide-react';
import logo from './assets/logo.png';
import EditModal from './components/EditModal';
import InfoModal from './components/InfoModal';
import ConfigModal from './components/ConfigModal';
import PokemonCard from './components/PokemonCard';
import CompareModal from './components/CompareModal';
import BenchmarkModal from './components/BenchmarkModal';
import LevelUpModal from './components/LevelUpModal';
import EeveelutionModal from './components/EeveelutionModal';
import GradeModal from './components/GradeModal';
import {
  ALL_SUBSKILLS, NATURES, INGREDIENT_ICONS, BERRY_ICONS, TYPE_ICONS, TYPE_TO_BERRY, BERRY_TO_TYPE, formatSkillName
} from './utils/gameData';

const API_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:8000`;

export default function App() {
  // --- SECURITY STATE ---
  // --- SECURITY STATE ---
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('sleepApp_apiKey') || "");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState("");
  const [isLoadingAuth, setIsLoadingAuth] = useState(false);

  const [pokedex, setPokedex] = useState({});
  const [overrides, setOverrides] = useState({});
  const [box, setBox] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Modals
  const [editingMon, setEditingMon] = useState(null);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [isBenchmarkModalOpen, setIsBenchmarkModalOpen] = useState(false);
  const [levelUpMon, setLevelUpMon] = useState(null);
  const [isGradeModalOpen, setIsGradeModalOpen] = useState(false);

  // Local Storage Initialization
  const [densityLevel, setDensityLevel] = useState(() => {
    const saved = localStorage.getItem('sleepApp_densityLevel');
    if (saved !== null) return Math.min(4, Math.max(1, parseInt(saved, 10)));
    return window.innerWidth < 768 ? 1 : 3;
  });
  const cardWidth = [120, 190, 220, 270][densityLevel - 1];

  const [sortMethod, setSortMethod] = useState(() => localStorage.getItem('sleepApp_sortMethod') || "date_desc");
  const [includeEvolutions, setIncludeEvolutions] = useState(() => {
    const saved = localStorage.getItem('sleepApp_includeEvolutions');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [filterUnlockedOnly, setFilterUnlockedOnly] = useState(() => {
    const saved = localStorage.getItem('sleepApp_filterUnlockedOnly');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [selectedMons, setSelectedMons] = useState(new Set());
  const [isComparing, setIsComparing] = useState(false);
  const [isEeveelutionModalOpen, setIsEeveelutionModalOpen] = useState(false);

  const selectedMonObjects = Array.from(selectedMons).map(uuid => box.find(m => m.uuid === uuid)).filter(Boolean);
  const isOnlyEeveeSelected = selectedMonObjects.length > 0 && selectedMonObjects.every(mon => mon.species_id && mon.species_id.includes("eevee"));

  // Filters
  const filterRef = useRef(null);
  const fileInputRef = useRef(null);
  const [filterIngredients, setFilterIngredients] = useState([]); 
  const [isIngFilterOpen, setIsIngFilterOpen] = useState(false);
  const [filterSubskills, setFilterSubskills] = useState([]);
  const [isSubFilterOpen, setIsSubFilterOpen] = useState(false);
  const [filterSpecialty, setFilterSpecialty] = useState([]);
  const [isSpecFilterOpen, setIsSpecFilterOpen] = useState(false);
  const [filterPokemonType, setFilterPokemonType] = useState([]);
  const [isTypeFilterOpen, setIsTypeFilterOpen] = useState(false);
  const [filterMainSkill, setFilterMainSkill] = useState([]);
  const [isMainSkillFilterOpen, setIsMainSkillFilterOpen] = useState(false);
  const [filterNature, setFilterNature] = useState({ up: null, down: null });
  const [isNatureFilterOpen, setIsNatureFilterOpen] = useState(false);
  const [filterBerry, setFilterBerry] = useState([]);
  const [isBerryFilterOpen, setIsBerryFilterOpen] = useState(false);
  const [filterDateStart, setFilterDateStart] = useState("");
  const [filterDateEnd, setFilterDateEnd] = useState("");
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
  const [filterRecentlyAdded, setFilterRecentlyAdded] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Local Storage Saving
  useEffect(() => { localStorage.setItem('sleepApp_densityLevel', densityLevel.toString()); }, [densityLevel]);
  useEffect(() => { localStorage.setItem('sleepApp_sortMethod', sortMethod); }, [sortMethod]);
  useEffect(() => { localStorage.setItem('sleepApp_includeEvolutions', JSON.stringify(includeEvolutions)); }, [includeEvolutions]);
  useEffect(() => { localStorage.setItem('sleepApp_filterUnlockedOnly', JSON.stringify(filterUnlockedOnly)); }, [filterUnlockedOnly]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setDensityLevel(prev => prev > 4 ? 3 : prev);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- AUTHENTICATION & DATA FETCHING ---
  useEffect(() => {
    if (apiKey && !isAuthenticated) {
      verifyAndLoadData(apiKey);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyAndLoadData = async (keyToTest) => {
    setIsLoadingAuth(true);
    setAuthError("");
    try {
      const res = await fetch(`${API_URL}/box`, {
        headers: { 'X-API-Key': keyToTest }
      });
      
      if (res.ok) {
        const boxData = await res.json();
        setBox(boxData);
        
        const dexRes = await fetch(`${API_URL}/pokedex`, {
          headers: { 'X-API-Key': keyToTest }
        });
        const dexData = await dexRes.json();
        setPokedex(dexData.pokedex);
        setOverrides(dexData.overrides);

        setIsAuthenticated(true);
        localStorage.setItem('sleepApp_apiKey', keyToTest);
      } else if (res.status === 401) {
        setIsAuthenticated(false);
        localStorage.removeItem('sleepApp_apiKey');
        setAuthError("Incorrect Master Password.");
      } else {
        setAuthError("Server error. Please try again.");
      }
    } catch (err) {
      setAuthError("Failed to connect to the server.");
    }
    setIsLoadingAuth(false);
  };

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    if (!apiKey.trim()) return;
    verifyAndLoadData(apiKey);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsIngFilterOpen(false); setIsSubFilterOpen(false); setIsSpecFilterOpen(false);
        setIsTypeFilterOpen(false); setIsMainSkillFilterOpen(false); setIsNatureFilterOpen(false); setIsBerryFilterOpen(false); setIsDateFilterOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- HANDLERS ---
  const getHeaders = () => ({
    'Content-Type': 'application/json',
    'X-API-Key': apiKey
  });

  const handleAuthError = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('sleepApp_apiKey');
    alert("Session expired or invalid key. Please log in again.");
  };

  const handleSave = async (mon, closeOnSave = true) => {
    try {
      const res = await fetch(`${API_URL}/box`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(mon)
      });
      if (res.status === 401) return handleAuthError();
      const data = await res.json();
      setBox(data.box);
      if (closeOnSave) {
        setEditingMon(null);
      }
    } catch (err) {
      alert("Failed to save Pokémon. Check connection.");
    }
  };

  const handleSavePokedex = async (speciesData) => {
    try {
      const res = await fetch(`${API_URL}/pokedex/${speciesData.id}`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(speciesData)
      });
      if (res.status === 401) return handleAuthError();
      if (res.ok) {
          const data = await res.json();
          setPokedex(data.pokedex);
          setOverrides(data.overrides);
      } else {
          alert("Failed to save species data.");
      }
    } catch (err) {
      alert("Connection error while saving Pokedex.");
    }
  };

  const handleDeletePokedex = async (speciesId) => {
    try {
      const res = await fetch(`${API_URL}/pokedex/${speciesId}`, { 
        method: 'DELETE',
        headers: getHeaders()
      });
      if (res.status === 401) return handleAuthError();
      if (res.ok) {
          const data = await res.json();
          setPokedex(data.pokedex);
          setOverrides(data.overrides);
          if (editingMon && editingMon.species_id === speciesId) setEditingMon(null);
      } else {
          const err = await res.json();
          alert(err.detail || "Failed to delete species.");
      }
    } catch (err) {
      alert("Connection error while deleting species.");
    }
  };

  const handleResetPokedex = async (speciesId) => {
    try {
      const res = await fetch(`${API_URL}/pokedex/${speciesId}/reset`, {
        method: 'POST',
        headers: getHeaders()
      });
      if (res.status === 401) return handleAuthError();
      if (res.ok) {
          const data = await res.json();
          setPokedex(data.pokedex);
          setOverrides(data.overrides);
      } else {
          alert("Failed to reset species.");
      }
    } catch (err) {
      alert("Connection error while resetting species.");
    }
  };

  const handleFilterBy = ({ type, value }) => {
    switch (type) {
      case 'search':
        setSearchTerm(prev => prev === value ? "" : value);
        break;
      case 'ingredient':
        setFilterIngredients(prev => prev.includes(value) ? prev.filter(i => i !== value) : [...prev, value]);
        break;
      case 'subskill':
        setFilterSubskills(prev => prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]);
        break;
      case 'specialty':
        setFilterSpecialty(prev => prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]);
        break;
      case 'type':
        setFilterPokemonType(prev => prev.includes(value) ? prev.filter(t => t !== value) : [...prev, value]);
        break;
      case 'mainSkill':
        setFilterMainSkill(prev => prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]);
        break;
      case 'nature':
        const natureStats = NATURES[value] || {};
        const targetUp = natureStats.up || 'None';
        const targetDown = natureStats.down || 'None';
        setFilterNature(prev => {
            if (prev.up === targetUp && prev.down === targetDown) return { up: null, down: null };
            return { up: targetUp, down: targetDown };
        });
        break;
      case 'berry':
        setFilterBerry(prev => prev.includes(value) ? prev.filter(b => b !== value) : [...prev, value]);
        break;
      default: break;
    }
  };

  const handleDelete = async (uuid) => {
    if (!window.confirm("Are you sure you want to delete this Pokémon? This cannot be undone.")) {
      return;
    }
    try {
      const res = await fetch(`${API_URL}/box/${uuid}`, { 
        method: 'DELETE',
        headers: getHeaders()
      });
      if (res.status === 401) return handleAuthError();
      if (res.ok) {
          const data = await res.json();
          setBox(data.box);
          if (selectedMons.has(uuid)) {
              const newSet = new Set(selectedMons);
              newSet.delete(uuid);
              setSelectedMons(newSet);
          }
      } else {
          alert("Could not delete Pokémon (Is it starred?)");
      }
    } catch (err) {
      alert("Connection error while deleting.");
    }
  };

  const handleClone = async (mon) => {
    const clone = { 
        ...mon, 
        uuid: uuidv4(), 
        nickname: mon.nickname ? `${mon.nickname} (Copy)` : `${pokedex[mon.species_id].name} (Copy)`,
          date_met: new Date().toISOString().split('T')[0],
          app_added_at: new Date().toISOString()
    };
    await handleSave(clone);
  };

  const handleBatchDelete = async () => {
    const deletableUUIDs = Array.from(selectedMons).filter(uuid => {
      const mon = box.find(m => m.uuid === uuid);
      return mon && !mon.is_favorite;
    });

    const starredCount = selectedMons.size - deletableUUIDs.length;

    if (deletableUUIDs.length === 0) {
      alert(`Cannot delete. All ${selectedMons.size} selected Pokémon are starred.`);
      return;
    }

    const confirmMsg = starredCount > 0 
      ? `You have ${selectedMons.size} selected, but ${starredCount} are starred and protected. Are you sure you want to delete the other ${deletableUUIDs.length} Pokémon?`
      : `Are you sure you want to delete these ${deletableUUIDs.length} Pokémon?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      for (const uuid of deletableUUIDs) {
          const res = await fetch(`${API_URL}/box/${uuid}`, { 
            method: 'DELETE',
            headers: getHeaders()
          });
          if (res.status === 401) return handleAuthError();
      }
      const res = await fetch(`${API_URL}/box`, { headers: getHeaders() });
      const data = await res.json();
      setBox(data);
      setSelectedMons(new Set()); 
    } catch (err) {
      alert("Connection error during batch delete.");
    }
  };

  const createNewMon = () => {
    const defaultId = pokedex['pikachu'] ? 'pikachu' : Object.keys(pokedex)[0];
    const species = pokedex[defaultId];
    if (!species) return;

    setEditingMon({
      uuid: uuidv4(),
      species_id: defaultId,
      nickname: "",
      level: 10,
      nature: "Hardy",
      subskills: ["", "", "", "", ""],
      specialty: species.specialty,
      berry: species.berry,
      carry_size: species.carrySize,
      frequency: species.frequency,
      ingredient_percentage: species.ingredientPercentage,
      ingredients: {
        "0": species.ingredientPools["0"][0],
        "30": species.ingredientPools["30"][0],
        "60": species.ingredientPools["60"][0]
      },
      is_shiny: false,
      is_favorite: false,
        date_met: new Date().toISOString().split('T')[0],
        app_added_at: new Date().toISOString()
    });
  };

  const toggleSelection = (uuid) => {
    const newSet = new Set(selectedMons);
    if (newSet.has(uuid)) newSet.delete(uuid);
    else newSet.add(uuid);
    setSelectedMons(newSet);
  };

  const handleExport = () => {
    const dataToExport = selectedMons.size > 0
      ? box.filter(m => selectedMons.has(m.uuid))
      : box;
    const dataStr = JSON.stringify(dataToExport, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pokemon_box_export_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const importedData = JSON.parse(e.target.result);
        if (!Array.isArray(importedData)) throw new Error("Invalid format");

        const validMons = [];
        const invalidMons = [];
        const sortedNatures = Object.keys(NATURES).sort();
        const sortedSkills = [...ALL_SUBSKILLS].sort();

        for (const mon of importedData) {
          let isInvalid = false;
          let fixedMon = { ...mon };
          
          if (!fixedMon.uuid) { fixedMon.uuid = uuidv4(); isInvalid = true; }

          const species = pokedex[fixedMon.species_id];
          if (!species) continue; // Unrecoverable without a valid species

          if (typeof fixedMon.level !== 'number' || fixedMon.level < 1 || fixedMon.level > 100) { fixedMon.level = 10; isInvalid = true; }
          if (!fixedMon.date_met || isNaN(new Date(fixedMon.date_met).getTime())) { fixedMon.date_met = new Date().toISOString().split('T')[0]; isInvalid = true; }
          if (!fixedMon.nature || !NATURES[fixedMon.nature]) { fixedMon.nature = sortedNatures[0]; isInvalid = true; }

          if (!Array.isArray(fixedMon.subskills) || fixedMon.subskills.length !== 5) {
            fixedMon.subskills = ["", "", "", "", ""];
            isInvalid = true;
          }
          
          let usedSkills = new Set(fixedMon.subskills.filter(s => ALL_SUBSKILLS.includes(s)));
          let fixedSubskills = [...fixedMon.subskills];
          for (let i = 0; i < 5; i++) {
            const s = fixedSubskills[i];
            if (s && !ALL_SUBSKILLS.includes(s)) {
              const nextSkill = sortedSkills.find(skill => !usedSkills.has(skill));
              fixedSubskills[i] = nextSkill || sortedSkills[0];
              usedSkills.add(fixedSubskills[i]);
              isInvalid = true;
            }
          }
          fixedMon.subskills = fixedSubskills;

          if (!fixedMon.ingredients || typeof fixedMon.ingredients !== 'object') { fixedMon.ingredients = {}; isInvalid = true; }
          ["0", "30", "60"].forEach(lvl => {
            const pool = species.ingredientPools?.[lvl] || [];
            const currentIng = fixedMon.ingredients[lvl];
            const isValidIng = currentIng && pool.some(i => i.name === currentIng.name && i.count === currentIng.count);
            if (!isValidIng && pool.length > 0) {
              fixedMon.ingredients[lvl] = pool[0];
              isInvalid = true;
            }
          });

          const existingMon = box.find(m => m.uuid === fixedMon.uuid);
          if (existingMon && existingMon.app_added_at) {
            fixedMon.app_added_at = existingMon.app_added_at;
          } else if (!fixedMon.app_added_at) { 
            fixedMon.app_added_at = new Date().toISOString(); 
          }

          if (isInvalid) invalidMons.push(fixedMon);
          else validMons.push(fixedMon);
        }

        let finalImportList = [...validMons];

        if (invalidMons.length > 0) {
          const confirmMsg = `${invalidMons.length} Pokémon have missing or incorrect data.\n\nWould you like to import them anyway using default values (today's date, alphabetical subskills/nature, default ingredients)?`;
          if (window.confirm(confirmMsg)) finalImportList = [...finalImportList, ...invalidMons];
        }

        if (finalImportList.length === 0) {
           alert("No valid Pokémon to import.");
           return;
        }

        const res = await fetch(`${API_URL}/box/batch`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(finalImportList)
        });
        if (res.status === 401) return handleAuthError();
        const data = await res.json();
        setBox(data.box);
        alert(`Import complete!\n\nAdded: ${data.added} new Pokémon\nUpdated: ${data.updated} existing Pokémon`);
      } catch (err) {
        alert("Failed to parse or import JSON file.");
      }
    };
    reader.readAsText(file);
    event.target.value = null; // Reset input
  };

  const findDuplicates = () => {
    const targetMons = selectedMons.size > 0 ? box.filter(m => selectedMons.has(m.uuid)) : box;
    const groups = {};
    
    targetMons.forEach(mon => {
      // Identical signature ignoring nickname, level, dates, and uuids
      const key = JSON.stringify({ species: mon.species_id, nature: mon.nature, subskills: mon.subskills, ingredients: mon.ingredients, shiny: mon.is_shiny });
      if (!groups[key]) groups[key] = [];
      groups[key].push(mon);
    });

    const newSelection = new Set();
    let duplicateCount = 0;

    Object.values(groups).forEach(group => {
      if (group.length > 1) {
        group.sort((a, b) => b.level - a.level); // Keep highest level unselected
        for (let i = 1; i < group.length; i++) { newSelection.add(group[i].uuid); duplicateCount++; }
      }
    });

    if (duplicateCount > 0) {
      setSelectedMons(newSelection);
      alert(`Found ${duplicateCount} exact duplicate(s)!\n\nThe lower-level duplicates have been automatically selected so you can quickly delete them.`);
    } else {
      alert("No duplicates found based on species, nature, subskills, and ingredients.");
    }
  };

  const closeAllDropdowns = () => {
    setIsIngFilterOpen(false); setIsSubFilterOpen(false); setIsSpecFilterOpen(false);
    setIsTypeFilterOpen(false); setIsMainSkillFilterOpen(false); setIsNatureFilterOpen(false); setIsBerryFilterOpen(false); setIsDateFilterOpen(false);
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setFilterIngredients([]); setFilterSubskills([]); setFilterNature({ up: null, down: null });
    setFilterSpecialty([]); setFilterPokemonType([]); setFilterMainSkill([]); setFilterBerry([]); setSelectedMons(new Set());
    setFilterDateStart(""); setFilterDateEnd("");
    setFilterRecentlyAdded(false);
  };

  const isMonRecentlyAdded = (mon) => {
    const addedAt = mon.app_added_at ? new Date(mon.app_added_at).getTime() : new Date('2023-07-16T00:00:00Z').getTime();
    return (Date.now() - addedAt) < 24 * 60 * 60 * 1000;
  };

  // --- FILTERING LOGIC ---
  const filteredBox = box.filter(mon => {
    const species = pokedex[mon.species_id];
    if (!species) return false;

    if (filterRecentlyAdded && !isMonRecentlyAdded(mon)) return false;

    const term = searchTerm.toLowerCase();
    let matchesText = searchTerm === "";
    if (searchTerm !== "") {
      const directMatch = species.name.toLowerCase().includes(term) || (mon.nickname && mon.nickname.toLowerCase().includes(term));
      if (directMatch) {
        matchesText = true;
      } else if (includeEvolutions && species.family) {
        const familyMembers = Object.values(pokedex).filter(p => p.family === species.family);
        matchesText = familyMembers.some(member => member.name.toLowerCase().includes(term));
      }
    }

    const matchesIngredients = filterIngredients.length === 0 || filterIngredients.some(ing => [
        mon.ingredients["0"].name,
        (!filterUnlockedOnly || mon.level >= 30) ? mon.ingredients["30"].name : null,
        (!filterUnlockedOnly || mon.level >= 60) ? mon.ingredients["60"].name : null
      ].includes(ing));

    const unlockLevels = [10, 25, 50, 75, 100];
    const matchesSubskills = filterSubskills.length === 0 || filterSubskills.every(skill => {
        if (!filterUnlockedOnly) return mon.subskills.includes(skill);
        return mon.subskills.some((s, idx) => s === skill && mon.level >= unlockLevels[idx]);
    });

    const matchesSpecialty = filterSpecialty.length === 0 || filterSpecialty.includes(species.specialty);
    const speciesType = BERRY_TO_TYPE[species.berry];
    const matchesType = filterPokemonType.length === 0 || filterPokemonType.includes(speciesType);
    const matchesMainSkill = filterMainSkill.length === 0 || filterMainSkill.includes(species.skill);
    const matchesBerry = filterBerry.length === 0 || filterBerry.includes(species.berry);

    let matchesNature = true;
    if (filterNature.up || filterNature.down) {
      const monStats = NATURES[mon.nature] || {};
      const monUp = monStats.up || 'None';
      const monDown = monStats.down || 'None';
      if (filterNature.up && monUp !== filterNature.up) matchesNature = false;
      if (filterNature.down && monDown !== filterNature.down) matchesNature = false;
    }

    let matchesDate = true;
    if (filterDateStart || filterDateEnd) {
      if (!mon.date_met) {
        matchesDate = false;
      } else {
        const met = new Date(mon.date_met).getTime();
        const start = filterDateStart ? new Date(filterDateStart).getTime() : -Infinity;
        const end = filterDateEnd ? new Date(filterDateEnd).getTime() : Infinity;
        if (met < start || met > end) matchesDate = false;
      }
    }

    return matchesText && matchesIngredients && matchesSubskills && matchesSpecialty && matchesType && matchesMainSkill && matchesNature && matchesBerry && matchesDate;
  });

  const speciesCount = box.reduce((acc, m) => {
    acc[m.species_id] = (acc[m.species_id] || 0) + 1;
    return acc;
  }, {});

  const sortedBox = [...filteredBox].sort((a, b) => {
    const speciesA = pokedex[a.species_id];
    const speciesB = pokedex[b.species_id];
    if (!speciesA || !speciesB) return 0;
    switch (sortMethod) {
      case "level_desc": return b.level - a.level;
      case "level_asc": return a.level - b.level;
      case "date_desc": return (b.date_met || "").localeCompare(a.date_met || "");
      case "date_asc": return (a.date_met || "").localeCompare(b.date_met || "");
      case "added_desc": return new Date(b.app_added_at || '2023-07-16T00:00:00Z').getTime() - new Date(a.app_added_at || '2023-07-16T00:00:00Z').getTime();
      case "added_asc": return new Date(a.app_added_at || '2023-07-16T00:00:00Z').getTime() - new Date(b.app_added_at || '2023-07-16T00:00:00Z').getTime();
      case "name": return speciesA.name.localeCompare(speciesB.name);
      case "pokedex_asc": {
        const dexDiff = (speciesA.pokedexNumber || 0) - (speciesB.pokedexNumber || 0);
        if (dexDiff !== 0) return dexDiff;
        const idDiff = a.species_id.localeCompare(b.species_id);
        if (idDiff !== 0) return idDiff;
        return b.level - a.level;
      }
      case "pokedex_desc": {
        const dexDiff = (speciesB.pokedexNumber || 0) - (speciesA.pokedexNumber || 0);
        if (dexDiff !== 0) return dexDiff;
        const idDiff = b.species_id.localeCompare(a.species_id);
        if (idDiff !== 0) return idDiff;
        return b.level - a.level;
      }
      case "quantity_desc": {
        const countDiff = (speciesCount[b.species_id] || 0) - (speciesCount[a.species_id] || 0);
        if (countDiff !== 0) return countDiff;
        const dexDiff = (speciesA.pokedexNumber || 0) - (speciesB.pokedexNumber || 0);
        if (dexDiff !== 0) return dexDiff;
        return a.species_id.localeCompare(b.species_id) || b.level - a.level;
      }
      case "quantity_asc": {
        const countDiff = (speciesCount[a.species_id] || 0) - (speciesCount[b.species_id] || 0);
        if (countDiff !== 0) return countDiff;
        const dexDiff = (speciesA.pokedexNumber || 0) - (speciesB.pokedexNumber || 0);
        if (dexDiff !== 0) return dexDiff;
        return a.species_id.localeCompare(b.species_id) || a.level - b.level;
      }
      default: return 0;
    }
  });

  const isAllVisibleSelected = sortedBox.length > 0 && sortedBox.every(m => selectedMons.has(m.uuid));

  const toggleSelectAll = () => {
    const visibleUUIDs = sortedBox.map(m => m.uuid);
    const newSet = new Set(selectedMons);
    if (isAllVisibleSelected) visibleUUIDs.forEach(uuid => newSet.delete(uuid));
    else visibleUUIDs.forEach(uuid => newSet.add(uuid));
    setSelectedMons(newSet);
  };

  const handleNavigateEdit = (direction) => {
    const currentIndex = sortedBox.findIndex(m => m.uuid === editingMon?.uuid);
    if (currentIndex === -1) return;
    let newIndex = currentIndex + direction;
    if (newIndex < 0) newIndex = sortedBox.length - 1;
    if (newIndex >= sortedBox.length) newIndex = 0;
    setEditingMon(sortedBox[newIndex]);
  };

  const editingIndex = editingMon ? sortedBox.findIndex(m => m.uuid === editingMon.uuid) : -1;
  const canNavigateEdit = editingIndex !== -1 && sortedBox.length > 1;

  // --- RENDER LOCK SCREEN ---
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 font-sans">
        <div className="bg-white p-8 md:p-12 rounded-3xl shadow-2xl w-full max-w-md text-center ring-1 ring-gray-900/5 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="bg-indigo-50 w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 shadow-inner">
             <Lock className="text-indigo-600" size={36} />
          </div>
          <h1 className="text-3xl font-black text-gray-800 tracking-tight mb-2">Personal Vault</h1>
          <p className="text-gray-500 font-medium mb-8 text-sm">Enter your Master Password to access your box.</p>
          
          <form onSubmit={handleLoginSubmit} className="space-y-4">
            <div className="relative">
              <input 
                type="password" 
                placeholder="Enter password..." 
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none font-bold text-gray-700 transition"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                autoFocus
              />
              <KeyRound className="absolute left-4 top-3.5 text-gray-400" size={20} />
            </div>
            
            {authError && (
              <p className="text-red-500 text-xs font-bold text-left animate-in fade-in bg-red-50 p-2 rounded border border-red-100">
                {authError}
              </p>
            )}

            <button 
              type="submit" 
              disabled={isLoadingAuth}
              className={`w-full py-3 rounded-xl font-black text-white shadow-lg transition-all
                ${isLoadingAuth ? 'bg-indigo-400 cursor-wait' : 'bg-indigo-600 hover:bg-indigo-700 hover:shadow-indigo-500/30'}
              `}
            >
              {isLoadingAuth ? 'Decrypting...' : 'Unlock Vault'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // --- RENDER MAIN APP ---
  return (
    <div className="min-h-[100dvh] bg-gray-100 pb-[calc(6rem+env(safe-area-inset-bottom))] font-sans text-gray-800">
      {(() => {
        const activeFilters = {
          searchTerm,
          ingredients: filterIngredients,
          subskills: filterSubskills,
          specialty: filterSpecialty,
          pokemonType: filterPokemonType,
          mainSkill: filterMainSkill,
          nature: filterNature,
          berry: filterBerry
        };
        return (
          <>
      {/* GLOBAL DARK HEADER */}
      <div className="sticky top-0 z-40">
      <div className="bg-gray-800 text-white shadow-md border-b border-gray-700 relative z-50">
        <div className="px-4 md:px-8 py-3 md:py-4 flex justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <img src={logo} alt="App Logo" className="w-10 h-10 md:w-12 md:h-12 object-contain drop-shadow-md" />
            <div>
              <h1 className="text-xl md:text-2xl font-black tracking-tight leading-none text-white">Pokémon Sleep Manager</h1>
              <p className="text-gray-400 text-[10px] md:text-xs font-bold uppercase tracking-widest mt-1">Production Calculator</p>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-3">
              <div className="relative w-64">
                <input type="text" placeholder="Search..." className="w-full pl-9 pr-4 py-2 bg-gray-700/50 border border-gray-600 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition shadow-sm text-sm text-white placeholder-gray-400" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                {searchTerm && <X className="absolute right-3 top-2.5 text-gray-400 cursor-pointer hover:text-white" size={16} onClick={() => setSearchTerm("")} />}
              </div>
              
              <button onClick={() => setIsInfoOpen(true)} className="bg-gray-700 text-gray-300 border border-gray-600 px-3 py-2 rounded-xl font-bold hover:bg-gray-600 hover:text-white transition shadow-sm" title="How the Calculator Works">
                  <Info size={18} />
              </button>

              <button onClick={() => setIsConfigOpen(true)} className="bg-gray-700 text-gray-300 border border-gray-600 px-3 py-2 rounded-xl font-bold hover:bg-gray-600 hover:text-white transition shadow-sm" title="Settings">
                  <SettingsIcon size={18} />
              </button>
            </div>

            <div className="hidden md:flex bg-gray-700 rounded-xl border border-gray-600 p-1 shadow-sm items-center">
              <button onClick={handleExport} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg transition" title="Export Box (or Selected)"><Upload size={18} /></button>
              <button onClick={() => fileInputRef.current?.click()} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg transition" title="Import Box"><Download size={18} /></button>
              <button onClick={findDuplicates} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg transition" title="Find & Select Duplicates"><Files size={18} /></button>
              <input type="file" ref={fileInputRef} onChange={handleImport} accept=".json" className="hidden" />
            </div>

            <button onClick={createNewMon} className="bg-indigo-600 text-white px-3 md:px-4 py-2 rounded-xl font-bold shadow-md hover:bg-indigo-500 transition flex items-center gap-1 md:gap-2 text-sm">
              <Plus size={18} /> <span className="hidden sm:inline">Add</span>
            </button>

            {/* Mobile Menu Toggle */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
              className={`md:hidden border border-gray-600 p-2 rounded-xl font-bold transition shadow-sm ${isMobileMenuOpen ? 'bg-gray-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'}`}
            >
              <Menu size={18} />
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-gray-800 border-t border-gray-700 p-4 flex flex-col gap-3 absolute w-full left-0 top-full shadow-xl z-50">
            <div className="flex gap-2">
              <button onClick={() => { setIsInfoOpen(true); setIsMobileMenuOpen(false); }} className="flex-1 bg-gray-700 text-gray-300 border border-gray-600 px-3 py-2 rounded-xl font-bold hover:bg-gray-600 hover:text-white transition shadow-sm flex items-center justify-center gap-2">
                  <Info size={18} /> Guide
              </button>
              <button onClick={() => { setIsConfigOpen(true); setIsMobileMenuOpen(false); }} className="flex-1 bg-gray-700 text-gray-300 border border-gray-600 px-3 py-2 rounded-xl font-bold hover:bg-gray-600 hover:text-white transition shadow-sm flex items-center justify-center gap-2">
                  <SettingsIcon size={18} /> Config
              </button>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { handleExport(); setIsMobileMenuOpen(false); }} className="flex-1 bg-gray-700 text-gray-300 border border-gray-600 px-2 py-2 rounded-xl font-bold hover:bg-gray-600 hover:text-white transition shadow-sm flex items-center justify-center gap-1.5 text-sm">
                  <Upload size={16} /> Export
              </button>
              <button onClick={() => { fileInputRef.current?.click(); setIsMobileMenuOpen(false); }} className="flex-1 bg-gray-700 text-gray-300 border border-gray-600 px-2 py-2 rounded-xl font-bold hover:bg-gray-600 hover:text-white transition shadow-sm flex items-center justify-center gap-1.5 text-sm">
                  <Download size={16} /> Import
              </button>
              <button onClick={() => { findDuplicates(); setIsMobileMenuOpen(false); }} className="flex-1 bg-gray-700 text-gray-300 border border-gray-600 px-2 py-2 rounded-xl font-bold hover:bg-gray-600 hover:text-white transition shadow-sm flex items-center justify-center gap-1.5 text-sm" title="Find Duplicates">
                  <Files size={16} /> Dupes
              </button>
            </div>
          </div>
        )}
      </div>

        {/* STICKY TOOLBAR */}
        <div
          ref={filterRef}
          className="bg-gray-100/95 backdrop-blur-md py-3 md:py-4 border-b border-gray-200/60 shadow-sm transition-all"
        >
          <div className="px-4 md:px-8 flex flex-col md:flex-row md:flex-wrap gap-3 md:gap-x-2 md:gap-y-3 md:items-center">
          <div className="flex items-center justify-between gap-1.5 w-full md:w-auto">
            {/* View Mode & Desktop Left Side Actions */}
            <div className="flex gap-1.5 items-center shrink-0">
              <div className="flex items-center gap-1.5 bg-white rounded-lg border border-gray-200 px-2.5 py-1.5 shrink-0" title={`Density Level: ${densityLevel}`}>
                <LayoutGrid size={12} className="text-gray-400 shrink-0" />
                <input
                  type="range"
                  min="1"
                  max="4"
                  step="1"
                  value={densityLevel}
                  onChange={(e) => setDensityLevel(parseInt(e.target.value))}
                  className="w-20 md:w-24 accent-indigo-600 cursor-pointer"
                />
                <Square size={17} className="text-gray-400 shrink-0" />
              </div>
              
              <div className="flex gap-1.5 items-center">
                <button 
                  onClick={toggleSelectAll}
                  disabled={sortedBox.length === 0}
                  className={`p-1.5 md:px-3 md:py-2 rounded-lg text-xs font-bold border transition flex items-center justify-center gap-2 shrink-0
                    ${isAllVisibleSelected ? 'bg-indigo-100 text-indigo-700 border-indigo-200' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'} ${sortedBox.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title="Select All"
                >
                  {isAllVisibleSelected ? <CheckSquare size={18} className="md:w-4 md:h-4" /> : <Square size={18} className="md:w-4 md:h-4" />}
                  <span className="hidden md:inline">Select All</span>
                </button>

                <button onClick={clearAllFilters} className="hidden md:block px-3 py-2 rounded-lg text-xs font-bold bg-white border border-gray-200 hover:bg-gray-50 text-gray-500 transition shrink-0">✕ Reset</button>
              </div>

              {selectedMons.size > 0 && (
                <div className="flex items-center gap-1 md:gap-2 ml-1 md:ml-2 animate-in fade-in slide-in-from-left-2">
                  <button onClick={() => setSelectedMons(new Set())} className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-full transition" title="Clear selection">
                    <X size={18} />
                  </button>
                  <span className="font-black text-indigo-700 text-sm whitespace-nowrap">
                    {selectedMons.size} Selected
                  </span>
                </div>
              )}
            </div>

            {/* Mobile Sort Dropdown */}
            <select value={sortMethod} onChange={(e) => setSortMethod(e.target.value)} className={`md:hidden bg-white px-1.5 py-1.5 rounded-lg border border-gray-200 text-[11px] font-bold text-gray-700 outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm cursor-pointer flex-1 min-w-0 truncate ${selectedMons.size > 0 ? 'hidden' : ''}`}>
              <option value="date_desc">Newest</option>
              <option value="date_asc">Oldest</option>
              <option value="added_desc">Added (New)</option>
              <option value="added_asc">Added (Old)</option>
              <option value="level_desc">Level (High)</option>
              <option value="level_asc">Level (Low)</option>
              <option value="name">Name (A-Z)</option>
              <option value="pokedex_asc">Dex No (Low)</option>
              <option value="pokedex_desc">Dex No (High)</option>
              <option value="quantity_desc">Qty (High)</option>
              <option value="quantity_asc">Qty (Low)</option>
            </select>

            {/* Mobile Action Tools */}
            <div className="flex md:hidden items-center gap-1.5 shrink-0">
              <button 
                onClick={() => setIsBenchmarkModalOpen(true)}
                className={`p-1.5 bg-white border border-indigo-200 text-indigo-500 hover:bg-indigo-50 hover:text-indigo-600 rounded-lg shadow-sm transition shrink-0 ${selectedMons.size > 0 ? 'hidden' : ''}`}
                title="Global Benchmark"
              >
                <Activity size={18} />
              </button>
              <button 
                onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
                className={`p-1.5 rounded-lg shadow-sm transition border shrink-0 ${isMobileFiltersOpen ? 'bg-gray-200 border-gray-300 text-gray-800' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                title="Filters"
              >
                <Filter size={18} />
              </button>
            </div>
          </div>

          {/* Left Vertical Separator */}
          <div className="hidden md:block border-l border-gray-300 h-8 mx-1"></div>

          {/* Action Tools Group */}
          <div className="hidden md:flex items-center justify-end gap-2 flex-grow w-full md:w-auto">
            <button 
              onClick={() => setIsBenchmarkModalOpen(true)}
              className={`px-4 py-2 rounded-xl text-sm font-bold shadow-sm transition flex items-center justify-center gap-1.5 whitespace-nowrap border w-full md:w-auto ${selectedMons.size > 0 ? 'bg-indigo-600 text-white hover:bg-indigo-700 border-transparent shadow-md' : 'bg-white border-indigo-200 text-indigo-500 hover:bg-indigo-50 hover:text-indigo-600'}`}
            >
              <Activity size={18} /> Benchmark
            </button>
            <button 
              onClick={() => setIsGradeModalOpen(true)}
              disabled={selectedMons.size === 0}
              className={`px-4 py-2 rounded-xl text-sm font-bold shadow-sm transition flex items-center justify-center gap-1.5 whitespace-nowrap border ${selectedMons.size > 0 ? 'bg-purple-600 text-white hover:bg-purple-700 border-transparent shadow-md' : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-70 border-transparent'}`}
            >
              <Award size={18} /> Grade
            </button>
            <button 
              onClick={() => setIsComparing(true)} 
              disabled={selectedMons.size < 2}
              className={`px-4 py-2 rounded-xl text-sm font-bold shadow-sm transition flex items-center justify-center gap-1.5 whitespace-nowrap border ${selectedMons.size >= 2 ? 'bg-teal-600 text-white hover:bg-teal-700 border-transparent shadow-md' : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-70 border-transparent'}`}
            >
              <Trophy size={18} /> Compare
            </button>
            <button 
              onClick={() => setIsEeveelutionModalOpen(true)} 
              disabled={!isOnlyEeveeSelected}
              className={`px-4 py-2 rounded-xl text-sm font-bold shadow-sm transition flex items-center justify-center gap-1.5 whitespace-nowrap border ${isOnlyEeveeSelected ? 'bg-pink-600 text-white hover:bg-pink-700 border-transparent shadow-md' : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-70 border-transparent'}`}
            >
              <Sparkles size={18} /> Eeveelutions
            </button>
            <div className="w-px h-6 bg-gray-300 mx-1"></div>
            <button 
              onClick={handleBatchDelete}
              disabled={selectedMons.size === 0}
              className={`px-4 py-2 rounded-xl text-sm font-bold shadow-sm transition flex items-center justify-center gap-1.5 whitespace-nowrap border ${selectedMons.size > 0 ? 'bg-red-50 border-red-200 text-red-600 hover:bg-red-100' : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-70 border-transparent'}`}
            >
              <Trash2 size={18} /> Delete
            </button>
          </div>

          {/* Right Vertical Separator */}
          <div className="hidden 2xl:block border-l border-gray-300 h-8 mx-1"></div>

          {/* Filters Block */}
          <div className={`${isMobileFiltersOpen ? 'flex' : 'hidden'} md:flex flex-wrap items-center gap-2 w-full md:flex-grow mt-2 md:mt-0 pt-2 md:pt-0 border-t md:border-t-0 border-gray-200 animate-in fade-in slide-in-from-top-2 md:slide-in-from-top-0`}>
            
          {/* Mobile-only Actions Dropdown Menu Items */}
          <div className="flex md:hidden w-full flex-col gap-2 pb-3 mb-1 border-b border-gray-200">
            <div className="relative w-full mb-1">
              <input type="text" placeholder="Search..." className="w-full pl-9 pr-4 py-2 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition shadow-sm text-sm text-gray-700 placeholder-gray-400" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
              {searchTerm && <X className="absolute right-3 top-2.5 text-gray-400 cursor-pointer hover:text-gray-600" size={16} onClick={() => setSearchTerm("")} />}
            </div>
            <div className="flex w-full gap-2">
              <button 
                onClick={toggleSelectAll}
                disabled={sortedBox.length === 0}
                className={`flex-1 px-3 py-2.5 rounded-lg text-sm font-bold border transition flex items-center justify-center gap-2
                  ${isAllVisibleSelected ? 'bg-indigo-100 text-indigo-700 border-indigo-200' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'} ${sortedBox.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isAllVisibleSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                Select All
              </button>

              <button onClick={clearAllFilters} className="flex-1 px-3 py-2.5 rounded-lg text-sm font-bold bg-white border border-gray-200 hover:bg-gray-50 text-gray-500 transition">✕ Reset</button>
            </div>
          </div>

          <div className="flex gap-1 bg-white p-1 rounded-lg border border-gray-200 shadow-sm shrink-0">
            <button onClick={() => setIncludeEvolutions(!includeEvolutions)} className={`px-3 py-1.5 rounded text-[10px] font-black uppercase tracking-wide transition ${includeEvolutions ? 'bg-indigo-100 text-indigo-700' : 'text-gray-400 hover:bg-gray-50'}`}>Family {includeEvolutions ? "ON" : "OFF"}</button>
            <div className="w-px bg-gray-200 mx-0.5"></div>
            <button onClick={() => setFilterUnlockedOnly(!filterUnlockedOnly)} className={`px-3 py-1.5 rounded text-[10px] font-black uppercase tracking-wide transition ${filterUnlockedOnly ? 'bg-indigo-100 text-indigo-700' : 'text-gray-400 hover:bg-gray-50'}`} title="Only match ingredients and sub-skills that are currently unlocked by level">Unlocked {filterUnlockedOnly ? "ON" : "OFF"}</button>
          </div>
          
          <div className="relative">
            <button onClick={() => { const o = isIngFilterOpen; closeAllDropdowns(); setIsIngFilterOpen(!o); }} className={`px-4 py-2 rounded-lg text-sm font-bold border transition ${filterIngredients.length > 0 ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-white text-gray-600 border-gray-200'}`}>🍎 Ingredients</button>
            {isIngFilterOpen && (
              <div className="absolute top-full mt-2 left-0 w-72 bg-white rounded-xl shadow-xl border border-gray-100 p-4 z-50 grid grid-cols-4 gap-2">
                {Object.keys(INGREDIENT_ICONS).map(ing => (
                  <button key={ing} onClick={() => filterIngredients.includes(ing) ? setFilterIngredients(prev => prev.filter(i => i !== ing)) : setFilterIngredients(prev => [...prev, ing])} className={`p-2 rounded-lg border flex items-center justify-center ${filterIngredients.includes(ing) ? 'bg-orange-50 border-orange-200' : 'border-transparent hover:bg-gray-50'}`}><img src={INGREDIENT_ICONS[ing]} alt={ing} className="w-8 h-8 object-contain" /></button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <button onClick={() => { const o = isBerryFilterOpen; closeAllDropdowns(); setIsBerryFilterOpen(!o); }} className={`px-4 py-2 rounded-lg text-sm font-bold border transition ${filterBerry.length > 0 ? 'bg-pink-100 text-pink-700 border-pink-200' : 'bg-white text-gray-600 border-gray-200'}`}>🍓 Berry</button>
            {isBerryFilterOpen && (
              <div className="absolute top-full mt-2 left-0 w-72 bg-white rounded-xl shadow-xl border border-gray-100 p-4 z-50 grid grid-cols-4 gap-2">
                {Object.keys(BERRY_ICONS).map(berry => (
                  <button key={berry} onClick={() => filterBerry.includes(berry) ? setFilterBerry(prev => prev.filter(b => b !== berry)) : setFilterBerry(prev => [...prev, berry])} className={`p-2 rounded-lg border flex flex-col items-center gap-1 ${filterBerry.includes(berry) ? 'bg-pink-50 border-pink-200' : 'border-transparent hover:bg-gray-50'}`} title={berry}>
                    <img src={BERRY_ICONS[berry]} alt={berry} className="w-7 h-7 object-contain" />
                    <span className="text-[9px] font-bold text-gray-500">{berry.charAt(0) + berry.slice(1).toLowerCase()}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <button onClick={() => { const o = isMainSkillFilterOpen; closeAllDropdowns(); setIsMainSkillFilterOpen(!o); }} className={`px-4 py-2 rounded-lg text-sm font-bold border transition flex items-center gap-2 ${filterMainSkill.length > 0 ? 'bg-indigo-100 text-indigo-700 border-indigo-200' : 'bg-white text-gray-600 border-gray-200'}`}><Wand2 size={16} /> Skill</button>
            {isMainSkillFilterOpen && (
              <div className="absolute top-full mt-2 left-0 w-64 bg-white rounded-xl shadow-xl border border-gray-100 p-2 z-50 max-h-60 overflow-y-auto flex flex-col gap-1">
                {Array.from(new Set(Object.values(pokedex).map(p => p.skill))).sort().map(skill => (
                  <button key={skill} onClick={() => filterMainSkill.includes(skill) ? setFilterMainSkill(prev => prev.filter(s => s !== skill)) : setFilterMainSkill(prev => [...prev, skill])} className={`px-3 py-2 rounded-lg text-left text-xs font-bold transition ${filterMainSkill.includes(skill) ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-gray-50 text-gray-600'}`}>{formatSkillName(skill)}</button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <button onClick={() => { const o = isSubFilterOpen; closeAllDropdowns(); setIsSubFilterOpen(!o); }} className={`px-4 py-2 rounded-lg text-sm font-bold border transition ${filterSubskills.length > 0 ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-white text-gray-600 border-gray-200'}`}>💎 Sub-skills</button>
            {isSubFilterOpen && (
              <div className="absolute top-full mt-2 left-0 w-64 bg-white rounded-xl shadow-xl border border-gray-100 p-2 z-50 max-h-60 overflow-y-auto flex flex-col gap-1">
                {ALL_SUBSKILLS.map(skill => (
                  <button key={skill} onClick={() => filterSubskills.includes(skill) ? setFilterSubskills(prev => prev.filter(s => s !== skill)) : setFilterSubskills(prev => [...prev, skill])} className={`px-3 py-2 rounded-lg text-left text-xs font-bold transition ${filterSubskills.includes(skill) ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-600'}`}>{skill}</button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <button onClick={() => { const o = isSpecFilterOpen; closeAllDropdowns(); setIsSpecFilterOpen(!o); }} className={`px-4 py-2 rounded-lg text-sm font-bold border transition ${filterSpecialty.length > 0 ? 'bg-green-100 text-green-700 border-green-200' : 'bg-white text-gray-600 border-gray-200'}`}>🏆 Specialty</button>
            {isSpecFilterOpen && (
              <div className="absolute top-full mt-2 left-0 w-48 bg-white rounded-xl shadow-xl border border-gray-100 p-2 z-50 flex flex-col gap-1">
                {["berry", "ingredient", "skill", "all"].map(spec => (
                  <button key={spec} onClick={() => filterSpecialty.includes(spec) ? setFilterSpecialty(prev => prev.filter(s => s !== spec)) : setFilterSpecialty(prev => [...prev, spec])} className={`px-3 py-2 rounded-lg text-left text-sm font-bold transition capitalize ${filterSpecialty.includes(spec) ? 'bg-green-50 text-green-700' : 'hover:bg-gray-50 text-gray-600'}`}>{spec}</button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <button onClick={() => { const o = isTypeFilterOpen; closeAllDropdowns(); setIsTypeFilterOpen(!o); }} className={`px-4 py-2 rounded-lg text-sm font-bold border transition ${filterPokemonType.length > 0 ? 'bg-red-100 text-red-700 border-red-200' : 'bg-white text-gray-600 border-gray-200'}`}>🔥 Type</button>
            {isTypeFilterOpen && (
              <div className="absolute top-full mt-2 left-0 w-64 bg-white rounded-xl shadow-xl border border-gray-100 p-4 z-50 grid grid-cols-4 gap-2">
                {Object.keys(TYPE_ICONS).map(type => (
                  <button key={type} onClick={() => filterPokemonType.includes(type) ? setFilterPokemonType(prev => prev.filter(t => t !== type)) : setFilterPokemonType(prev => [...prev, type])} className={`p-2 rounded-lg border flex items-center justify-center ${filterPokemonType.includes(type) ? 'bg-red-50 border-red-200' : 'border-transparent hover:bg-gray-50'}`} title={`${type} (Found Berry: ${TYPE_TO_BERRY[type]})`}><span className="text-xl">{TYPE_ICONS[type]}</span></button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <button onClick={() => { const o = isNatureFilterOpen; closeAllDropdowns(); setIsNatureFilterOpen(!o); }} className={`px-4 py-2 rounded-lg text-sm font-bold border transition ${(filterNature.up || filterNature.down) ? 'bg-purple-100 text-purple-700 border-purple-200' : 'bg-white text-gray-600 border-gray-200'}`}>🌿 Nature</button>
            {isNatureFilterOpen && (
              <div className="absolute top-full mt-2 left-0 w-64 bg-white rounded-xl shadow-xl border border-gray-100 p-4 z-50 space-y-4">
                <div><div className="text-[10px] font-bold text-gray-400 uppercase mb-2">Boosts (▲)</div><div className="flex flex-wrap gap-1">{["Speed", "Ing", "Skill", "Energy", "Exp"].map(stat => (<button key={stat} onClick={() => setFilterNature(prev => ({...prev, up: prev.up === stat ? null : stat, down: prev.down === 'None' ? null : prev.down}))} className={`px-2 py-1 text-xs rounded border ${filterNature.up === stat ? 'bg-red-50 text-red-600 border-red-200' : 'bg-gray-50'}`}>{stat}</button>))}</div></div>
                <div><div className="text-[10px] font-bold text-gray-400 uppercase mb-2">Reduces (▼)</div><div className="flex flex-wrap gap-1">{["Speed", "Ing", "Skill", "Energy", "Exp"].map(stat => (<button key={stat} onClick={() => setFilterNature(prev => ({...prev, down: prev.down === stat ? null : stat, up: prev.up === 'None' ? null : prev.up}))} className={`px-2 py-1 text-xs rounded border ${filterNature.down === stat ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-gray-50'}`}>{stat}</button>))}</div></div>
                <div className="pt-2 border-t border-gray-100">
                  <button onClick={() => setFilterNature(prev => (prev.up === 'None' && prev.down === 'None' ? {up: null, down: null} : {up: 'None', down: 'None'}))} className={`w-full py-1.5 text-xs font-bold rounded border transition ${filterNature.up === 'None' && filterNature.down === 'None' ? 'bg-purple-50 text-purple-600 border-purple-200' : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border-transparent'}`}>Neutral Nature</button>
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <button onClick={() => { const o = isDateFilterOpen; closeAllDropdowns(); setIsDateFilterOpen(!o); }} className={`px-4 py-2 rounded-lg text-sm font-bold border transition ${(filterDateStart || filterDateEnd) ? 'bg-cyan-100 text-cyan-700 border-cyan-200' : 'bg-white text-gray-600 border-gray-200'}`}>📅 Date</button>
            {isDateFilterOpen && (
              <div className="absolute top-full mt-2 left-0 w-48 bg-white rounded-xl shadow-xl border border-gray-100 p-4 z-50 space-y-3">
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">From</label>
                  <input type="date" value={filterDateStart} onChange={(e) => setFilterDateStart(e.target.value)} className="w-full p-2 text-xs border border-gray-200 rounded bg-gray-50 text-gray-700 outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">To</label>
                  <input type="date" value={filterDateEnd} onChange={(e) => setFilterDateEnd(e.target.value)} className="w-full p-2 text-xs border border-gray-200 rounded bg-gray-50 text-gray-700 outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div className="pt-1">
                  <button onClick={() => { setFilterDateStart(""); setFilterDateEnd(""); }} className="w-full py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-bold rounded transition">Clear</button>
                </div>
              </div>
            )}
          </div>

          <button 
            onClick={() => { closeAllDropdowns(); setFilterRecentlyAdded(!filterRecentlyAdded); }} 
            className={`px-4 py-2 rounded-lg text-sm font-bold border transition ${filterRecentlyAdded ? 'bg-pink-100 text-pink-700 border-pink-200 shadow-inner' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 shadow-sm'} ${densityLevel <= 5 ? 'hidden md:block' : ''}`}
          >
            ✨ New
          </button>

          <div className="hidden md:block flex-grow"></div>
          <select value={sortMethod} onChange={(e) => setSortMethod(e.target.value)} className="hidden md:block bg-white px-3 py-2 rounded-lg border border-gray-200 text-sm font-bold text-gray-700 outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm cursor-pointer w-auto">
            <option value="date_desc">Date Met (Newest)</option>
            <option value="date_asc">Date Met (Oldest)</option>
            <option value="added_desc">Date Added (Newest)</option>
            <option value="added_asc">Date Added (Oldest)</option>
            <option value="level_desc">Level (High-Low)</option>
            <option value="level_asc">Level (Low-High)</option>
            <option value="name">Name (A-Z)</option>
            <option value="pokedex_asc">Pokedex No. (Low-High)</option>
            <option value="pokedex_desc">Pokedex No. (High-Low)</option>
            <option value="quantity_desc">Quantity (High-Low)</option>
            <option value="quantity_asc">Quantity (Low-High)</option>
          </select>
          </div>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-6 pt-4 md:pt-6">

        {/* POKEMON GRID */}
        <div 
          className={`grid ${densityLevel === 1 ? 'gap-1' : densityLevel <= 3 ? 'gap-2' : 'gap-3'}`}
          style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${cardWidth}px, 1fr))` }}
        >
          {sortedBox.map(mon => (
            <PokemonCard 
              key={mon.uuid}
              mon={mon}
              pokedex={pokedex}
              densityLevel={densityLevel}
              isSelected={selectedMons.has(mon.uuid)}
            isRecentlyAdded={isMonRecentlyAdded(mon)}
              onSelect={() => toggleSelection(mon.uuid)}
              onEdit={setEditingMon}
              onDelete={handleDelete}
              onSave={handleSave}
              onClone={handleClone}
              onLevelUp={setLevelUpMon}
              activeFilters={activeFilters}
              onFilterBy={handleFilterBy}
              onClearFilters={clearAllFilters}
            />
          ))}
        </div>

        {/* MOBILE BOTTOM ACTION SHEET */}
        {selectedMons.size > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 md:hidden shadow-[0_-4px_16px_rgba(0,0,0,0.08)] pb-[max(0.5rem,env(safe-area-inset-bottom))] animate-in slide-in-from-bottom-full duration-200">
            <div className="flex items-center justify-between px-2 py-2">
              <div className="flex flex-col items-center justify-center w-16 shrink-0">
                <span className="text-xl font-black text-indigo-600 leading-none">{selectedMons.size}</span>
                <span className="text-[8px] font-bold text-gray-500 uppercase tracking-wider mt-1">Selected</span>
              </div>
              <div className="w-px h-10 bg-gray-200 shrink-0 mx-1"></div>
              <div className="flex items-center justify-around flex-1 px-1">
                <button onClick={() => setIsBenchmarkModalOpen(true)} className="flex flex-col items-center justify-center gap-1 p-2 text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors min-w-[60px]">
                  <Activity size={20} />
                  <span className="text-[9px] font-bold">Benchmark</span>
                </button>
                <button onClick={() => setIsComparing(true)} disabled={selectedMons.size < 2} className={`flex flex-col items-center justify-center gap-1 p-2 rounded-xl transition-all min-w-[60px] ${selectedMons.size >= 2 ? 'text-teal-600 hover:bg-teal-50' : 'text-gray-400 opacity-50 cursor-not-allowed'}`}>
                  <Trophy size={20} />
                  <span className="text-[9px] font-bold">Compare</span>
                </button>
                <button onClick={() => setIsEeveelutionModalOpen(true)} disabled={!isOnlyEeveeSelected} className={`flex flex-col items-center justify-center gap-1 p-2 rounded-xl transition-all min-w-[60px] ${isOnlyEeveeSelected ? 'text-pink-600 hover:bg-pink-50' : 'text-gray-400 opacity-50 cursor-not-allowed'}`}>
                  <Sparkles size={20} />
                  <span className="text-[9px] font-bold">Eeveelutions</span>
                </button>
                <button onClick={handleBatchDelete} className="flex flex-col items-center justify-center gap-1 p-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors min-w-[60px]">
                  <Trash2 size={20} />
                  <span className="text-[9px] font-bold">Delete</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MODALS */}
        {editingMon && (
          <EditModal 
            key={editingMon.uuid} 
            mon={editingMon} 
            pokedex={pokedex} 
            onClose={() => setEditingMon(null)} 
            onSave={handleSave} 
            onNext={canNavigateEdit ? () => handleNavigateEdit(1) : null}
            onPrev={canNavigateEdit ? () => handleNavigateEdit(-1) : null}
          />
        )}
        {isInfoOpen && <InfoModal onClose={() => setIsInfoOpen(false)} />}
        {isConfigOpen && <ConfigModal pokedex={pokedex} overrides={overrides} onClose={() => setIsConfigOpen(false)} onSavePokedex={handleSavePokedex} onDeletePokedex={handleDeletePokedex} onResetPokedex={handleResetPokedex} />}
        
        <LevelUpModal isOpen={!!levelUpMon} onClose={() => setLevelUpMon(null)} mon={levelUpMon} pokedex={pokedex} />

        {/* Pass down selectedMons as an array so BenchmarkModal can pick it up */}
        <BenchmarkModal
          isOpen={isBenchmarkModalOpen}
          onClose={() => setIsBenchmarkModalOpen(false)}
          pokedex={pokedex}
          box={box}
          selectedMons={Array.from(selectedMons)}
        />

        {isComparing && (
          <CompareModal 
            selectedMons={box.filter(m => selectedMons.has(m.uuid))} 
            pokedex={pokedex} 
            onClose={() => setIsComparing(false)} 
            onRemove={(uuid) => {
              const newSet = new Set(selectedMons);
              newSet.delete(uuid);
              setSelectedMons(newSet);
              if (newSet.size < 2) setIsComparing(false); 
            }}
          />
        )}

        <EeveelutionModal
          isOpen={isEeveelutionModalOpen}
          onClose={() => setIsEeveelutionModalOpen(false)}
          selectedMons={box.filter(m => selectedMons.has(m.uuid))}
          onRemove={(uuid) => {
            const newSet = new Set(selectedMons);
            newSet.delete(uuid);
            setSelectedMons(newSet);
            if (newSet.size === 0) setIsEeveelutionModalOpen(false);
          }}
        />

        <GradeModal
          isOpen={isGradeModalOpen}
          onClose={() => setIsGradeModalOpen(false)}
          selectedMons={box.filter(m => selectedMons.has(m.uuid))}
          pokedex={pokedex}
        />

      </div>
          </>
        );
      })()}
    </div>
  );
}